/**
 * Composition picker — modal dialog that lists an order's compositions and
 * resolves a callback when the user picks one.
 *
 * Static API:
 *   CompositionPickerView.open({
 *     order:           <Backbone.Model>     // order/offer model that exposes `compositions` array
 *     product:         <Backbone.Model?>    // optional, used for nicer print-area names
 *     attachmentName:  <string?>            // shown in the hint
 *     title:           <string?>            // window title (defaults to "Pick a composition")
 *     onPick:          function(composition) // called with the picked composition object
 *   });
 */
define('compositionPickerView',[
  'module',
  'marionette',
  'underscore',
  'settings',
  'rolesMixin',
  'template!compositionPickerView'
], function (
  Module,
  Marionette,
  _,
  Settings,
  RolesMixin,
  Tpl
) {
  'use strict';

  var letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M',
    'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];

  function getPrintAreaName(composition, product) {
    if (composition.paName) return composition.paName;
    var paCode = composition.paCode;
    var cAccount = Settings.get('currentCAccount');
    if (cAccount && cAccount.displayPaCodeLetter) {
      var letter = letters[parseInt(paCode, 10) - 1];
      if (letter) paCode = letter;
    }
    if (product) {
      var pa = _.findWhere(product.get('printAreas'), { id: composition.paId });
      if (!pa) pa = _.findWhere(product.get('printAreas'), { code: composition.paCode });
      if (pa && pa.name) return pa.name + ' (' + paCode + ')';
    }
    return paCode || '';
  }

  function getPrintZoneName(composition, product) {
    if (composition.itName) return composition.itName;
    if (product) {
      var pa = _.findWhere(product.get('printAreas'), { id: composition.paId });
      if (pa) {
        var pz = _.findWhere(pa.printZones, { id: composition.pzId });
        if (pz && pz.name) return pz.name;
      }
    }
    return composition.itCode || '';
  }

  function getThumbUrl(composition) {
    var params = {
      pac: composition.paCode,
      t: new Date().getTime()
    };
    if (!composition.useBackgroundColor) params.nobg = true;
    var roles = Settings.get('cAccountRoles') || {};
    var rel = roles[RolesMixin.DASHBOARD_ORDERS_DESIGN2] ?
      'compositions/' + composition.secId + '/snapshot' :
      'compositions/' + composition.secId + '/snapshot/withMeasure';
    return Settings.url('x2png', rel, params);
  }

  var View = Marionette.ItemView.extend({
    template: Tpl,
    className: 'composition-picker-view',

    events: {
      'click .composition-picker-item': 'onPick',
      'click .composition-picker-cancel': 'onCancel'
    },

    serializeData: function () {
      var compositions = (this.options.order && this.options.order.get('compositions')) || [];
      var product = this.options.product;

      // Hold a reference so onPick can resolve by index — that's more robust than
      // re-looking-up by secId (which can be coerced to a number by jQuery.data
      // and miss the string-keyed entry).
      this._compositions = compositions;

      var listForTemplate = _.map(compositions, function (c) {
        return {
          secId: c.secId,
          printAreaName: getPrintAreaName(c, product),
          printZoneName: getPrintZoneName(c, product),
          thumbUrl: getThumbUrl(c)
        };
      });

      return {
        compositions: listForTemplate,
        attachmentFileName: this.options.attachmentName || ''
      };
    },

    onPick: function (e) {
      var index = parseInt($(e.currentTarget).attr('data-index'), 10);
      var picked = (this._compositions && !isNaN(index)) ? this._compositions[index] : null;
      var onPick = this.options.onPick;

      // Always close the dialog first, then invoke the callback — that way a
      // throw inside the consumer's callback can't leave the dialog stuck open.
      this.closeWindow();

      if (picked && typeof onPick === 'function') {
        try {
          onPick(picked);
        } catch (err) {
          if (window.console && console.error) console.error('composition picker onPick threw:', err);
        }
      }
    },

    onCancel: function () {
      this.closeWindow();
    },

    closeWindow: function () {
      if (this.windowView) this.windowView.close();
    }
  });

  View.open = function (data) {
    require([
      'windows',
      'compositionPickerView'
    ], function (Windows, ViewCtor) {
      // Pick a translated label that matches the tab name the user sees in the order detail.
      var tabName = _.i18n('orders.composition.tabTitle', null, 'Personalisation');
      var defaultTitle = _.i18n('compositionPicker.title', null, 'Pick a ' + tabName.toLowerCase());

      Windows.open({
        view: function () { return new ViewCtor(data); },
        id: 'composition-picker:' + (data && data.order ? data.order.get('purchaseOrderId') : Date.now()),
        title: (data && data.title) || defaultTitle,
        modal: true,
        width: 640,
        height: 520
      });
    });
  };

  Module.exports = View;
});

